import { connectRevitGrpc, getConnectGeneration } from '../api/grpc/revitClient';
import { getGrpcBaseUrl, isGrpcRuntimeAvailable } from '../api/grpc/grpcRuntime';
import { DEVICE_VERIFICATION_ERROR, PLUGIN_UPDATE_ERROR, normalizeMachineFingerprint } from '../lib/machineFingerprint';
import type { AuthGet, AuthSet } from './authStoreTypes';

let fingerprintVerification = 0;
let verifiedTarget: string | null = null;

export type VerifiedIdentity = {
  target: string;
  fingerprint: string;
  verification: number;
  generation: number;
};

export function invalidateFingerprint(): void {
  fingerprintVerification++;
  verifiedTarget = null;
}

export function clearVerifiedTarget(): void {
  verifiedTarget = null;
}
export function isCurrentIdentity(get: AuthGet, identity: VerifiedIdentity): boolean {
  const state = get();
  return identity.target === getGrpcBaseUrl()
    && identity.target === verifiedTarget
    && identity.verification === fingerprintVerification
    && identity.generation === getConnectGeneration()
    && !state.fingerprintLoading
    && !state.fingerprintError
    && identity.fingerprint === normalizeMachineFingerprint(state.machineFingerprint);
}

export async function initializeFingerprint(
  set: AuthSet,
  options: { force?: boolean } = {}
): Promise<boolean> {
  const verification = ++fingerprintVerification;
  const target = getGrpcBaseUrl();
  verifiedTarget = null;
  set({ machineFingerprint: null, fingerprintLoading: true, fingerprintError: null });
  let fingerprint: string | null = null;
  let message = DEVICE_VERIFICATION_ERROR;

  try {
    if (isGrpcRuntimeAvailable() && target) {
      const request = connectRevitGrpc(options);
      const generation = getConnectGeneration();
      const result = await request;
      if (generation !== getConnectGeneration()) message = DEVICE_VERIFICATION_ERROR;
      else if (result.success) {
        fingerprint = normalizeMachineFingerprint(result.fingerprint);
        if (!fingerprint) message = PLUGIN_UPDATE_ERROR;
      } else {
        message = result.error || DEVICE_VERIFICATION_ERROR;
      }
    }
  } catch {
    message = DEVICE_VERIFICATION_ERROR;
  }

  if (verification !== fingerprintVerification || target !== getGrpcBaseUrl()) return false;
  verifiedTarget = fingerprint ? target : null;
  set({
    machineFingerprint: fingerprint,
    fingerprintLoading: false,
    fingerprintError: fingerprint ? null : message,
  });
  return fingerprint !== null;
}

export async function requireRuntimeIdentity(get: AuthGet): Promise<VerifiedIdentity | null> {
  const state = get();
  // An explicit verification invocation, not submission, recovers a failed probe.
  if (state.fingerprintLoading || state.fingerprintError) return null;

  const target = getGrpcBaseUrl();
  if (!target || !await state.initializeFingerprint() || target !== getGrpcBaseUrl()) return null;
  const fingerprint = normalizeMachineFingerprint(get().machineFingerprint);
  if (!fingerprint) return null;

  const identity = { target, fingerprint, verification: fingerprintVerification, generation: getConnectGeneration() };
  return isCurrentIdentity(get, identity) ? identity : null;
}
